import Folder from "@mui/icons-material/Folder";
import { Box, Button, Card, Chip, Link, Paper, Skeleton, Typography } from "@mui/material";
import { useId } from "react";
import { Link as RouterLink } from "react-router";
import { ProgramIcon } from "../../programs/components/program-icon";
import { useRecentPrograms } from "../hooks/use-recent-programs";

import type { FC } from "react";

/** The programs most recently created Communications in, as a way back into them. */
export const RecentProgramsCard: FC = () => {
  const gradientId = useId();
  const { data: programs, error } = useRecentPrograms();

  if (error) return null;

  return (
    <Card sx={{ p: 2, height: "100%" }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Paper
          elevation={2}
          sx={{ display: "inline-flex", alignItems: "center", gap: 1, px: 1.5, py: 0.75 }}
        >
          <svg
            width="0"
            height="0"
            aria-hidden="true"
            focusable="false"
            style={{ position: "absolute" }}
          >
            <defs>
              <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#00B6EF" />
                <stop offset="100%" stopColor="#C24599" />
              </linearGradient>
            </defs>
          </svg>
          <Folder sx={{ fontSize: 18, fill: `url(#${gradientId})` }} />
          <Typography variant="subtitle2">Recent Programs</Typography>
        </Paper>
        {Boolean(programs?.length) && (
          <Link component={RouterLink} to="/tabbed-programs" variant="subtitle2" underline="hover">
            View all
          </Link>
        )}
      </Box>

      {programs === null ? (
        <Box display="flex" flexWrap="wrap" gap={1}>
          {[160, 140, 120].map((width) => (
            <Skeleton key={width} variant="rounded" width={width} height={32} />
          ))}
        </Box>
      ) : programs.length === 0 ? (
        <Box textAlign="center">
          <Typography variant="body2" textAlign="left" mb={3}>
            Programs enable you to organise your Communications into groups. Your most recently used
            Programs will appear here!
          </Typography>
          <Button
            variant="contained"
            href="/moments"
            data-analytics-id="dashboard-recent-programs-create"
          >
            Create your first Communication
          </Button>
        </Box>
      ) : (
        <Box display="flex" flexWrap="wrap" gap={1}>
          {programs.map(({ id, title, colour, icon }) => (
            <Chip
              key={id}
              component={RouterLink}
              to={`/tabbed-programs/${id}`}
              clickable
              color="primary"
              variant="outlined"
              icon={
                // ProgramIcon's Tooltip drops the class Chip gives its icon, so a span takes it.
                <Box component="span" display="inline-flex">
                  <ProgramIcon data={icon ?? undefined} fontSize="small" sx={{ color: colour }} />
                </Box>
              }
              label={title}
              // MUI's outlined chip tucks its icon in close to the border; give it room.
              sx={{ "& .MuiChip-icon": { ml: 1, mr: -0.25 } }}
            />
          ))}
        </Box>
      )}
    </Card>
  );
};
